import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from "react-redux";
import { useDebouncedCallback } from 'use-debounce';

import { FormHelperText, makeStyles, TextField} from '@material-ui/core';

import generateId from '../../utils/generateId';
import makePath from '../../utils/makePath';
import { setItem } from "../../actions";
import { useDirty, useFormElementCleanUp, useValidation } from '../../hooks';
import { CharCountHelpText } from '.';

const defaultCriteria = {
  mustChange: true,
  optional: false,
}

const useStyles = makeStyles(theme => ({
  root: {
    margin: 10,
    width: '100%',
    maxWidth: 415,
    display: 'flex',
    flexDirection: 'column'
  },
  fullWidth: {
    maxWidth: 'none',
  },
  noMargin: {
    margin: 0,
  }
}))

export const TextInput = React.memo(({absoluteMax,callback,defaultText = '',given,max,name,noMargin,updateParent,validationCriteria=defaultCriteria,...props}) => {
  
  const classes = useStyles();
  const dispatch = useDispatch();
  const setDirty = useDirty();


  // state
  const [value, setValue] = useState(defaultText.toString());
  const [id] = useState(generateId('textInput-'));

  const path = makePath(props.path,name);  
  useFormElementCleanUp(path);

  
  // Validation conditions
  const validate = () => {

    if (!value && !validationCriteria.optional) {
      setError('Missing! This field is required.');
    } else if (value === defaultText && validationCriteria.mustChange) {
      setError('Missing! You need to replace the default text.');
    } else if (max && value.length > max ) {
      setError('Error! You have exceeded the character limit.');
    } else {
      setError('')
    }
    
  }
  
  // Apply validation conditions
  const [error, setError] = useValidation(validate,value,path,id);


  // absoluteMax does not allow user to type more.
  // max just displays char count in red if user goes over
  if (absoluteMax) {
    max = absoluteMax;
  }

  // Delay callback while user types to avoid  
  // overwhelming the browser with rerenders
  const debounced = useDebouncedCallback(
    (value) => {
      if (updateParent) {
        updateParent({[name]: value});
      } else {
        dispatch(setItem(path,value));
        if (callback) {
          callback(name, value);
        }
      }
    },
    // delay in ms
    200
  );


  // Update store and/or parent whenever state changes.
  // Include all values from useDebouncedCallback in dependencies
  // because useDebouncedCallback has no dependencies array
  useEffect(() => {
    debounced.callback(value)
  }, [callback,debounced,dispatch,name,path,value,updateParent])

  // handle user inputs
  const handleChange = (e) => {
    const value = e.target.value;
    if ( (absoluteMax && value.length <= absoluteMax) || !absoluteMax ) {
      setDirty();
      setValue(value);
    } 
  }

  // Set state from given value, if provided
  useEffect(() => {
    if (given) {
      setValue(given);
    }
  }, [given]);
  
  delete props.path;

  return (
    <div className={`${classes.root} ${props.fullWidth ? classes.fullWidth : ''} ${noMargin ? classes.noMargin : ''}`}>
      <TextField 
        error={!!error}
        variant="outlined"
        autoComplete="off"
        id={id}
        name={name}
        value={value}
        onChange={handleChange} 
        helperText={error}
        InputLabelProps={props.placeholder ? {shrink: true} : {} }
        InputProps={{'aria-describedby': `${id}-helper-text ${id}-additional-helper-text ${id}-char-count`}}
        {...props}
      />
      
      {/* Additional help text so that built-in "helperText" can be used for error */}
      {props.help ? 
        <FormHelperText id={`${id}-additional-helper-text`}>
          {props.help}
        </FormHelperText>
      : '' }

      <CharCountHelpText length={value.length} max={max} id={id}/> 

    </div>
  )

})

TextInput.propTypes = {
  callback: PropTypes.func,
  help: PropTypes.oneOfType([PropTypes.string,PropTypes.node]),
  label: PropTypes.string, 
  name: PropTypes.string.isRequired,
  onBlue: PropTypes.func,
  updateParent: PropTypes.func
}